/**
 * The selected survey's card: its head, and its questions grouped the way the backend groups them.
 *
 * ── The grouping is `item.section`, and the order is the catalog's ──────────────────────────
 * `StandardQuestionCatalog` names five: `personal`, `health`, `training`, `wellbeing` and
 * `additional`. A trainer's OWN question carries no section at all — the backend resolves it to null
 * — so `additional` is where an absent section lands rather than a sixth case. The order below is
 * KANON's own stacking and the catalog's own listing, which agree; it is declared rather than
 * derived, so a section the wire invents later appears at the end instead of silently first.
 *
 * ── This card is READ-ONLY, and that is a decision rather than an omission ───────────────────
 * KANON `01-ankiety` draws a toggle on every row and a „3/4" beside every section head, because the
 * canvas drew the list and the editor on one artboard. Here the ONE way to change a form is the
 * pencil, which opens `/raporty/$id` — so a row states what the question is and the section states
 * how many questions it holds. Two controls that both change a form, a centimetre apart, is the pair
 * a trainer presses the wrong half of; and a toggle that wrote on press would contradict the
 * editor's own rule, where including a question is a checkbox confirmed by „Zapisz zmiany" because
 * the PATCH replaces the whole item list at once.
 *
 * ── A standard question is named from its CODE, never from the wire ──────────────────────────
 * `SurveyQuestionView.label` is `StandardQuestion.DefaultLabel`, which the backend keeps in English
 * on purpose and expects every client to replace. This card printed it, so a Polish trainer read
 * „Date of birth" under a Polish section head. `shared/lib/standardQuestionNames` is the one place
 * that reading lives; the SHORT name is what a row takes, because a row is a name in a list and not
 * the question as the phone asks it.
 *
 * ── The mapping note is drawn where the answer LEAVES the survey ─────────────────────────────
 * KANON puts „→ Ciało" on „Waga startowa" and on nothing else — not on the date of birth, the
 * gender or the height, all of which also map. Read literally, the note marks an answer that becomes
 * a row a trainer reads on ANOTHER screen: a body measurement. The profile fields fill the profile
 * the card is about, and the goal question's own type pill already says „Kreator celu". So the note
 * is the three body targets and nothing else, and this comment is the record in case the canon is
 * later read as annotating every mapped question.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import type {
  TrainWithMeModulesSurveysDtosResponsesSurveyTemplateResponse as SurveyTemplateResponse,
  TrainWithMeModulesSurveysDtosSurveyQuestionView as SurveyQuestionView,
} from '~/shared/api/generated/model';

import { questionNames } from '~/shared/lib/standardQuestionNames';

import { questionGlyph, type QuestionGlyphKey } from './questionGlyphs';
import type { TranslateKey } from './translateKey';

/** The catalog's sections, in the order the card stacks them. `additional` also absorbs a null. */
export const SURVEY_SECTIONS = [
  'personal',
  'health',
  'training',
  'wellbeing',
  'additional',
] as const;

export type SurveySection = (typeof SURVEY_SECTIONS)[number];

/** One row under a section head. */
export interface DetailQuestion {
  /** The template item's own id, or its code where it has none — a custom question has both. */
  readonly key: string;
  readonly label: string;
  /** `intakeSurvey.answerType.*`, mobile's verbatim copy. `null` where the wire named no type. */
  readonly typeLabelKey: string | null;
  /** „→ Ciało", or `null`. See the header for why so few rows carry one. */
  readonly mapNoteKey: string | null;
  readonly required: boolean;
  /**
   * The mark drawn before the label, or `null` for a question the trainer wrote themselves.
   *
   * The EDITOR's standard rows draw the same marks from the same mapping — the owner asked for them
   * on the editor and the two surfaces list the same rows, so a mark on one and not the other would
   * be the pair disagreeing about one form. `model/questionGlyphs` is the single decision.
   */
  readonly glyph: QuestionGlyphKey | null;
}

export interface DetailSection {
  readonly section: SurveySection;
  readonly questions: readonly DetailQuestion[];
}

/** The three destinations a trainer reads on another screen. See the header. */
const BODY_TARGETS = new Set(['InitialWeight', 'BodyMeasurement', 'CompositeMeasurement']);

/**
 * The section a question belongs to, with an unrecognised name folded into „Dodatkowe".
 *
 * Exported because the EDITOR groups by it too, and the fallback is the whole point: the two
 * surfaces have to agree about where a question the catalog has newly sectioned ends up, or one of
 * them lists it and the other silently draws no row for it at all.
 */
export function sectionOf(raw: string | null | undefined): SurveySection {
  return (SURVEY_SECTIONS as readonly string[]).includes(raw ?? '')
    ? (raw as SurveySection)
    : 'additional';
}

function questionOf(item: SurveyQuestionView, index: number, t: TranslateKey): DetailQuestion {
  const answerType = item.answerType ?? '';
  return {
    // The id is the item's identity where it has one. A standard question is identified by its
    // code, and the index is the last resort so two unnamed rows never collide on one React key.
    key: item.id ?? item.code ?? `row-${String(index)}`,
    label: questionNames(t, { code: item.code, label: item.label ?? '' }).shortLabel,
    typeLabelKey: answerType === '' ? null : `intakeSurvey.answerType.${answerType}`,
    mapNoteKey: BODY_TARGETS.has(item.mapTarget ?? '') ? 'web:reports.surveys.detail.mapBody' : null,
    required: item.required === true,
    glyph: questionGlyph(item.code, answerType, item.vitalType),
  };
}

/**
 * The card's sections, in order, with the empty ones left out.
 *
 * A section with no questions is not drawn: a head reading „Zdrowie · 0" over nothing says a form is
 * missing something rather than that the trainer composed it that way.
 */
export function surveySections(
  template: SurveyTemplateResponse | null | undefined,
  t: TranslateKey,
): readonly DetailSection[] {
  const items = template?.items ?? [];
  const grouped = new Map<SurveySection, DetailQuestion[]>();

  items.forEach((item, index) => {
    const section = sectionOf(item.section);
    const bucket = grouped.get(section) ?? [];
    bucket.push(questionOf(item, index, t));
    grouped.set(section, bucket);
  });

  return SURVEY_SECTIONS.flatMap((section) => {
    const questions = grouped.get(section);
    return questions === undefined || questions.length === 0 ? [] : [{ section, questions }];
  });
}

/** „Dane osobowe" and the line under it. Web copy: the canon's own words, not mobile's. */
export function sectionTitle(section: SurveySection, t: TranslateKey): string {
  return t(`web:reports.surveys.section.${section}.title`);
}

export function sectionSubtitle(section: SurveySection, t: TranslateKey): string {
  return t(`web:reports.surveys.section.${section}.subtitle`);
}

/**
 * The intake card's one line under its name: when the form is filled, and how many of its questions
 * are fixed. The figure is COUNTED from the rows the card draws with a lock, never written into the
 * copy — the copy said „Cztery" while the catalog had grown to six locked questions.
 */
export function intakeLede(sections: readonly DetailSection[], t: TranslateKey): string {
  const count = sections.reduce(
    (sum, entry) => sum + entry.questions.filter((question) => question.required).length,
    0,
  );
  return t('web:reports.surveys.detail.intakeLede', { count });
}
